import React, {useEffect, useState } from 'react';
import { Grid, Box, Switch, Typography, Tooltip, IconButton, Icon} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import { useStyles } from '../styles';
import EditIcon from '../../../icons/editIcon.svg';
import { PmConfigType } from '../pmConfigType';
import { AppConfigSettingsViewEnum } from '../applicationsConfigSettings';
import { useDispatch, useSelector } from 'react-redux';
import { CustomQuestionTypeEnum } from '@inosio/payments-sdk';
import InputConfig from './InputConfig';
import { getUpdatePropertyGroupConfigs, selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface ApplicationCardsProps {
  pmConfig: PmConfigType;
  handleConfigChange: (ConfigTypeKey1: string, AssignedValue1: any, ConfigTypeKey2?: string, AssignedValue2?: any) => void;
  setView: React.Dispatch<React.SetStateAction<AppConfigSettingsViewEnum>>;
}

export default function ApplicationCards({ pmConfig, handleConfigChange, setView }: ApplicationCardsProps) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { selectedPropertyGroupAssignedConfigs } = useSelector(selectPropertyGroupDetailsState);
  const questions = selectedPropertyGroupAssignedConfigs.ApplicationsQuestions ?? [];
  const personalQuestions = questions.filter((q) => q.CustomQuestionType === CustomQuestionTypeEnum.PersonalV1);
  const employeeQuestions = questions.filter((q) => q.CustomQuestionType === CustomQuestionTypeEnum.EmployeeV1);
  const petsQuestions = questions.filter((q) => q.CustomQuestionType === CustomQuestionTypeEnum.PetV1);
  const additionalOccupantsQuestions = questions.filter((q) => q.CustomQuestionType === CustomQuestionTypeEnum.AdditionalOccupantV1);
  const vehiclesQuestions = questions.filter((q) => q.CustomQuestionType === CustomQuestionTypeEnum.VehicleV1);
  const customQuestions = questions.filter((q) => q.CustomQuestionType === CustomQuestionTypeEnum.CustomV1);
  const rentalHistoryQuestions = questions.filter((q) => q.CustomQuestionType === CustomQuestionTypeEnum.RentalHistoryV1);
  const applicationsUrlLink = selectedPropertyGroupAssignedConfigs?.Uuid ? `${window.location.origin}/apply?organization=${selectedPropertyGroupAssignedConfigs.Uuid}` : '';

  return (
    <>
      <Box className={classes.configContainer} sx={{ mb: 0 }}>
        <Box className={classes.configHeaderContainer} sx={{ display: 'flex' }}>
          <Box sx={{ display: 'flex', width: 200, alignItems: 'center' }}>
            {getTypography('Applicant Data', 600, '18px')}

            <Tooltip title="Applicant Data" placement="right">
              <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
            </Tooltip>
          </Box>

          <Typography
            sx={{
              fontFamily: 'Work Sans',
              fontWeight: 400,
              fontSize: '18px',
              lineHeight: '22px',
              my: 1,
            }}
          >
            Please ensure that your custom application questions and data fields are inline with{' '}
            <a href="https://www.hud.gov/program_offices/fair_housing_equal_opp/fair_housing_act_overview#_What_Is_Prohibited" target="blank" style={{ color: '#172B85', textDecoration: 'underline', cursor: 'pointer' }}>
              Fair Housing Act guidelines.
            </a>
          </Typography>
        </Box>

        <Box className={classes.configHeaderContainer} sx={{ display: 'flex' }}>
          <Box sx={{ display: 'flex', width: 200, alignItems: 'center' }}>
            {getTypography('Custom Application URL:', 300, '14px')}

            {/* <Tooltip title="Applicant Data" placement="right">
              <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
            </Tooltip> */}
          </Box>
          <Typography
            style={{
              fontFamily: 'Work Sans',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#000000',
              marginRight: '40px',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {applicationsUrlLink}
          </Typography>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="Copy quick pay link"
            onClick={() => {
              navigator.clipboard.writeText(applicationsUrlLink);
            }}
          >
            <ContentCopyIcon sx={{ width: '22px', height: '22px', fill: '#14142B' }} />
          </IconButton>
        </Box>

        <Box className={classes.configBodyContainer} sx={{ px: 1, pb: 3 }}>
          <Grid container>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <ApplicationCard
                title={'Personal Information'}
                onClickEdit={() => setView(AppConfigSettingsViewEnum.PersonalInformation)}
                tooltipText={'Personal details like name and current address that will be provided with your application.'}
                customQuestionsCount={personalQuestions?.length}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <ApplicationCard
                title={'Employment & Income'}
                onClickEdit={() => setView(AppConfigSettingsViewEnum.EmploymentAndIncome)}
                tooltipText={'Details about your income and how you will pay rent at your new home.'}
                customQuestionsCount={employeeQuestions?.length}
                yearsConfigName={'ApplicationsEmploymentYearsRequired'}
                requiredYears={selectedPropertyGroupAssignedConfigs.ApplicationsEmploymentYearsRequired}
                handleConfirmYearsChange={(value) => {
                  dispatch(
                    getUpdatePropertyGroupConfigs({
                      propertyGroupUuid: selectedPropertyGroupAssignedConfigs.Uuid,
                      productTypeId: 2,
                      updatedPmConfig: {
                        ApplicationsEmploymentYearsRequired: value,
                      },
                    }),
                  );
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <ApplicationCard 
                title={'Animals'} 
                onClickEdit={() => setView(AppConfigSettingsViewEnum.Pets)} 
                tooltipText={'Details about animals that you live with. Not all properties accept animals, but we will warn you when you apply.'} 
                customQuestionsCount={petsQuestions?.length}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <ApplicationCard
                title={'Non-Adult Occupants'}
                onClickEdit={() => setView(AppConfigSettingsViewEnum.AdditionalOccupants)}
                tooltipText={`Non-adult people that you live with. You can also include adults that won't be included on the lease. If you need another occupant to qualify for the property, they will need to submit their own application.`}
                showCheckBox={true}
                checked={!!pmConfig.ApplicationsAllowAdditionalOccupants}
                handleCheckbox={(checked) => handleConfigChange('ApplicationsAllowAdditionalOccupants', checked)}
                customQuestionsCount={additionalOccupantsQuestions?.length}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <ApplicationCard
                title={'Vehicles'}
                onClickEdit={() => setView(AppConfigSettingsViewEnum.Vehicles)}
                tooltipText={'Details about cars, trucks, and/or other vehicles that will be near or on the property.'}
                showCheckBox={true}
                checked={!!pmConfig.ApplicationsAllowVehicles}
                handleCheckbox={(checked) => handleConfigChange('ApplicationsAllowVehicles', checked)}
                customQuestionsCount={vehiclesQuestions?.length}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <ApplicationCard
                title={'Custom Questions'}
                onClickEdit={() => setView(AppConfigSettingsViewEnum.CustomQuestions)}
                tooltipText={'Add questions and additional data that you wanted collected as part of your application.'}
                showCheckBox={true}
                checked={!!pmConfig.ApplicationsEnableCustomQuestionsSection}
                handleCheckbox={(checked) => handleConfigChange('ApplicationsEnableCustomQuestionsSection', checked)}
                customQuestionsCount={customQuestions?.length}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <ApplicationCard
                title={'Rental History'}
                onClickEdit={() => setView(AppConfigSettingsViewEnum.RentalHistory)}
                tooltipText={'Details about your rental history.'}
                customQuestionsCount={rentalHistoryQuestions?.length}
                yearsConfigName={'ApplicationsRentalHistoryYearsRequired'}
                requiredYears={selectedPropertyGroupAssignedConfigs.ApplicationsRentalHistoryYearsRequired}
                handleConfirmYearsChange={(value) => {
                  dispatch(
                    getUpdatePropertyGroupConfigs({
                      propertyGroupUuid: selectedPropertyGroupAssignedConfigs.Uuid,
                      productTypeId: 2,
                      updatedPmConfig: {
                        ApplicationsRentalHistoryYearsRequired: value,
                      },
                    }),
                  );
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
}

const getTypography = (text: string, fontWeight: number, fontSize: string, restProps?: object) => (
  <Typography
    sx={{
      fontFamily: 'Work Sans',
      fontWeight: { fontWeight },
      fontSize: { fontSize },
      lineHeight: '22px',
      ...restProps,
    }}
  >
    {text}
  </Typography>
);

interface ApplicationCardProps {
  title: string;
  tooltipText: string;
  showCheckBox?: boolean;
  checked?: boolean;
  customQuestionsCount?: number;
  yearsConfigName?: string;
  requiredYears?: number;
  handleConfirmYearsChange?: (AssignedValue: any) => void;
  onClickEdit: () => void;
  handleCheckbox?: (checked: boolean) => void;
}

const ApplicationCard = ({ title, tooltipText, showCheckBox, checked, customQuestionsCount, yearsConfigName, requiredYears, handleConfirmYearsChange, onClickEdit, handleCheckbox}: ApplicationCardProps) => {
  const classes = useStyles();
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    setIsChecked(!!checked);
  }, [checked]);

  const [yearsValue, setYearsValue] = useState(0);

  useEffect(() => {
    if (requiredYears) setYearsValue(requiredYears);
  }, [requiredYears]);

  const handleYearsChange = (ConfigTypeKey: string, AssignedValue: any) => {
    setYearsValue(AssignedValue);
    handleConfirmYearsChange && handleConfirmYearsChange(AssignedValue);
  };

  const [yearsError, setYearsError] = useState('');
  const handleYearsError = (key: string, value: string) => {
    setYearsError(value);
  };

  return (
    <Box className={classes.configContainer} sx={{ mb: 0 }}>
      <Box className={classes.configHeaderContainer} sx={{ alignItems: 'start', justifyContent: 'space-between', minHeight: 72, p: 2, pr: 0.5 }}>
        <Typography
          sx={{
            fontFamily: 'Work Sans',
            fontWeight: '600',
            fontSize: '18px',
            lineHeight: '22px',
          }}
        >
          {title}
          <Tooltip title={tooltipText} placement="right">
            <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
          </Tooltip>
        </Typography>

        <Box sx={{ mt: -2, display: 'flex', flexDirection: 'column' }}>
          {showCheckBox ? (
            <Switch
              checked={isChecked}
              onChange={(e) => {
                setIsChecked(e.target.checked);
                handleCheckbox && handleCheckbox(e.target.checked);
              }}
            />
          ) : (
            <Box width="58px" height="38px" />
          )}

          <Box sx={{ textAlign: 'right', mr: 1 }}>
            <IconButton onClick={onClickEdit} sx={{ p: 0.25, mr: '-2px' }}>
              <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img
                  style={{
                    cursor: 'pointer',
                    width: '18px',
                    height: '18px',
                  }}
                  src={EditIcon}
                  alt="Edit"
                />
              </Icon>
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box className={classes.configBodyContainer} sx={{ minHeight: 115, justifyContent: 'center' }}>
        <Grid container sx={{ p: 2, pt: 2, pb: 1, position: 'relative' }}>
          {yearsConfigName && <InputConfig name={yearsConfigName} label="Years Required" value={yearsValue} onChange={handleYearsChange} error={yearsError} onError={handleYearsError} />}
          <Typography
            sx={{
              fontFamily: 'Work Sans',
              fontWeight: '400',
              fontSize: '20px',
              lineHeight: '18px',
              mt: 1,
              mb: 2,
            }}
          >
            {`${customQuestionsCount ?? 0} Custom Fields`}
          </Typography>
        </Grid>
      </Box>
    </Box>
  );
};
